/**
 * SDCX 改键矩阵：多媒体(0x30)、宏(0x60)、Profile(0x51)
 * 仅覆盖改键面板 EXTRA_KEYS_MATRIX_SDCX 中已暴露的 UI 键码（多媒体 / 宏）
 */

export type SdcxMatrixCellLike = {
  type: number;
  code1: number;
  code2: number;
  code3: number;
};

/** 消费类 / 多媒体（协议 Type=0x30，Code1=CodeL，Code2=CodeH） */
export const SDCX_CONSUMER_TYPE = 0x30;

/** 宏按键（Macro_index, 播放次数, Type） */
export const SDCX_MACRO_TYPE = 0x60;

/** Profile 切换 */
export const SDCX_PROFILE_TYPE = 0x51;

/** 协议最多 32 宏槽；改键面板 M0–M15 共 16 槽 */
export const SDCX_MACRO_SLOT_COUNT = 32;
export const SDCX_MACRO_UI_SLOT_COUNT = 16;
export const SDCX_MACRO_UI_BASE = 62720;

/**
 * UI 多媒体键码 → [code1, code2]
 * 与 Consumer Page (0x0C) 及 ZHWX 面板键码一致
 */
export const SDCX_UI_TO_CONSUMER_CELL: Readonly<
  Record<number, readonly [number, number]>
> = {
  8303: [0x6f, 0x00],
  8304: [0x70, 0x00],
  8373: [0xb5, 0x00],
  8374: [0xb6, 0x00],
  8375: [0xb7, 0x00],
  8397: [0xcd, 0x00],
  8418: [0xe2, 0x00],
  8425: [0xe9, 0x00],
  8426: [0xea, 0x00],
  8579: [0x83, 0x01],
  8594: [0x92, 0x01],
  8596: [0x94, 0x01],
  8739: [0x23, 0x02],
};

const SDCX_CONSUMER_CELL_TO_UI = new Map<string, number>(
  Object.entries(SDCX_UI_TO_CONSUMER_CELL).map(([ui, [code1, code2]]) => [
    `${code2 & 0xff}-${code1 & 0xff}`,
    Number(ui),
  ])
);

/** code1 仅低字节时的兜底（code2=0） */
export const SDCX_CONSUMER_CODE1_TO_UI: Readonly<Record<number, number>> = {
  0x6f: 8303,
  0x70: 8304,
  0xb5: 8373,
  0xb6: 8374,
  0xb7: 8375,
  0xcd: 8397,
  0xe2: 8418,
  0xe9: 8425,
  0xea: 8426,
  0x83: 8579,
};

/** Profile 动作码 → UI（与星闪 61957–61960 语义对齐：+ / - / 循环 / 切换） */
export const SDCX_PROFILE_CODE1_TO_UI: Readonly<Record<number, number>> = {
  0x01: 61957,
  0x02: 61958,
  0x03: 61959,
  0x04: 61960,
};

export const SDCX_UI_TO_PROFILE_CODE1: Readonly<Record<number, number>> =
  Object.fromEntries(
    Object.entries(SDCX_PROFILE_CODE1_TO_UI).map(([code, ui]) => [
      ui,
      Number(code),
    ])
  ) as Readonly<Record<number, number>>;

export function resolveSdcxConsumerUiKeyCodeFromCell(
  code1: number,
  code2 = 0
): number {
  const ui = SDCX_CONSUMER_CELL_TO_UI.get(`${code2 & 0xff}-${code1 & 0xff}`);
  if (ui != null) return ui;
  if (!code2) return SDCX_CONSUMER_CODE1_TO_UI[code1 & 0xff] ?? 0;
  return 0;
}

/**
 * 生成宏矩阵单元（协议 0x60：Macro_index / 播放次数 / loopType）。
 * 公版抓包 M0→空格：CMD 0x10 @0x0118，DATA `00 00 00 60 00 01 00`。
 * slot 为 0 基 UI 槽号（M0→0）。
 */
export function buildSdcxMacroMatrixCell(
  slot: number,
  playCount = 1,
  loopType = 0
): SdcxMatrixCellLike {
  const safeSlot =
    Math.max(0, Math.min(SDCX_MACRO_SLOT_COUNT - 1, slot)) & 0xff;
  return {
    type: SDCX_MACRO_TYPE,
    code1: safeSlot,
    code2: Math.max(1, Math.min(255, Math.round(playCount))) & 0xff,
    code3: Math.max(0, Math.min(3, Math.round(loopType))) & 0xff,
  };
}

export function isSdcxMacroMatrixCell(cell: SdcxMatrixCellLike): boolean {
  return cell.type === SDCX_MACRO_TYPE;
}

export function isSdcxConsumerMatrixCell(cell: SdcxMatrixCellLike): boolean {
  return cell.type === SDCX_CONSUMER_TYPE;
}

export function isSdcxProfileMatrixCell(cell: SdcxMatrixCellLike): boolean {
  return cell.type === SDCX_PROFILE_TYPE;
}
